// @ts-check

/**
 * @file Renames an existing auth token.
 */

import {useLocalSearchParams, useRouter} from "expo-router"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {memo, useEffect} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native-propforge"
import Button from "../../../src/components/button"
import Card from "../../../src/components/card"
import ScreenTitle from "../../../src/components/screen-title"
import Text from "../../../src/components/text"
import TextInput from "../../../src/components/text-input"
import {UserAuthToken} from "../../../src/frontend-models/user-auth-token"
import errorMessage from "../../../src/shared/error-message"
import {requiredRouteParam} from "../../../src/shared/route-params"
import {useScreenLoading} from "../../../src/components/board-layout/screen-loading"
import useThemeColors, {useThemeName} from "../../../src/shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */

/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{[key: string]: unknown}} AuthTokenEditScreenProps */

/**
 * @typedef {object} AuthTokenEditScreenState
 * @property {boolean} loading Whether the token is loading.
 * @property {string} name Editable token label.
 * @property {boolean} saving Whether the rename is saving.
 * @property {{kind: "error" | "success", text: string} | null} status Screen status message.
 */

/**
 * Auth token rename screen.
 * @augments {ShapeComponent<AuthTokenEditScreenProps, AuthTokenEditScreenState>}
 */
class AuthTokenEditScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params

  /** @type {ReturnType<typeof useRouter>} */
  router

  /** @type {string} */
  tokenId

  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()

  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /**
   * Latest text typed into the name field. The input is uncontrolled
   * (`defaultValue` from the loaded token) so fast WebDriver `sendKeys` cannot
   * leave the value half-applied; we read this at submit time.
   * @type {string}
   */
  enteredName = ""

  /** @type {AuthTokenEditScreenState} */
  state = {
    loading: true,
    name: "",
    saving: false,
    status: null
  }

  loadRequestId = 0

  /** Initializes route params and token loading. */
  setup() {
    useLocale()
    useScreenLoading(this.s.loading)
    const params = useLocalSearchParams()
    const tokenId = requiredRouteParam(params, "id", "Auth token ID")
    this.params = params
    this.router = useRouter()
    this.tokenId = tokenId
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    useEffect(() => {
      void this.loadToken()
    }, [tokenId])
  }

  /**
   * Renders the rename form.
   * @returns {ReactNode} Auth token rename content.
   */
  render() {
    const {loading, name, saving, status} = this.s
    const colors = this.tt.colors
    const themeName = this.tt.themeName

    if (loading) {
      return (
        <View
          style={styles.loadingScreen ||= {flex: 1}}
          testID="authTokenEditScreen"
        />
      )
    }

    return (
      <View
        style={styles.screen ||= {flex: 1}}
        testID="authTokenEditScreen"
      >
        <ScreenTitle>{_("Rename auth token")}</ScreenTitle>
        <Card
          style={styles.formCard ||= {
            gap: 12,
            maxWidth: 520,
            width: "100%"
          }}
          testID="authTokenEditScreen/form"
        >
          <Text
            style={styles[`label-${themeName}`] ||= {
              color: colors.textMuted,
              fontWeight: 600
            }}
            testID="authTokenEditScreen/form/nameLabel"
          >
            {_("Token name")}
          </Text>
          <TextInput
            defaultValue={name}
            onChangeText={this.tt.onNameChangeText}
            onSubmitEditing={this.tt.onSavePress}
            style={styles.nameInput ||= {fontSize: 16}}
            testID="authTokenEditScreen/form/nameInput"
          />
          {status &&
            <Text
              style={styles[`status-${themeName}-${status.kind}`] ||= {
                color: status.kind === "success" ? colors.successText : colors.dangerText,
                fontWeight: 600
              }}
              testID="authTokenEditScreen/form/status"
            >
              {status.text}
            </Text>
          }
          <Button
            disabled={saving}
            iconName="floppy-disk"
            label={saving ? _("Saving...") : _("Save")}
            onPress={this.tt.onSavePress}
            testID="authTokenEditScreen/form/saveButton"
          />
        </Card>
      </View>
    )
  }

  /**
   * Loads the auth token being renamed.
   * @returns {Promise<void>} Resolves when token loading completes.
   */
  loadToken = async () => {
    const requestId = ++this.loadRequestId
    try {
      const token = await UserAuthToken
        .query()
        .select(["id", "name"])
        .find(this.tokenId)
      if (this.loadRequestId !== requestId) return
      this.enteredName = token.name()
      this.s.name = token.name()
      this.s.loading = false
    } catch (error) {
      if (this.loadRequestId !== requestId) return
      this.s.loading = false
      this.s.status = {
        kind: "error",
        text: `${_("Could not load auth token")}: ${errorMessage(error)}`
      }
    }
  }

  /**
   * Records the latest token name without binding the input's value.
   * @param {string} value Label text from the input.
   * @returns {void} Nothing.
   */
  onNameChangeText = (value) => {
    this.enteredName = value
    if (this.s.status) this.s.status = null
  }

  /**
   * Saves the renamed auth token.
   * @returns {Promise<void>} Resolves when save handling completes.
   */
  onSavePress = async () => {
    if (this.s.saving) return
    const name = this.enteredName.trim()
    if (!name) {
      this.s.status = {kind: "error", text: _("A name is required")}
      return
    }
    this.s.saving = true
    this.s.status = null
    try {
      const token = await UserAuthToken.find(this.tokenId)
      await token.update({name})
      this.router.push("/auth-tokens?saved=1")
    } catch (error) {
      this.s.saving = false
      this.s.status = {kind: "error", text: errorMessage(error)}
    }
  }
}

/** @type {NamedExoticComponent<AuthTokenEditScreenProps>} */
const AuthTokenEditScreenComponent = memo(shapeComponent(AuthTokenEditScreen))

export default AuthTokenEditScreenComponent
